import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import {
  Alert,
  Checkbox,
  Col,
  message,
  PageHeader,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import {
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/es";
import {
  ButtonItem,
  ErrorItem,
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  dineroDisabledMask,
} from "../../../../Components/Items";
import ModalDescargarDocumentos from "../../../../Components/ModalDescargarDocumentos";
import descargarConciliacionMasivo from "../../../../Components/DescargarConciliacionMasivo";
import {
  COLOR_ESTATUS,
  ETIQUETAS_ESTATUS,
  ESTADO_FACTURA,
  descargarExcel,
  descargarPdf,
  etiquetaFormaDePago,
  excelConciliacionBlob,
} from "./exportarConciliacion";

moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;

const PAGINACION = [25, 50, 100, 200];

// Las casillas del ZIP: factura y complementos por separado, porque en una
// auditoria muchas veces sólo hacen falta los XML.
const OPCIONES_DESCARGA = [
  { clave: "incluirXmlFactura", etiqueta: "XML de la factura", porOmision: true },
  { clave: "incluirPdfFactura", etiqueta: "PDF de la factura", porOmision: true },
  {
    clave: "incluirXmlComplemento",
    etiqueta: "XML de los complementos",
    descripcion: "Las facturas PUE no llevan complemento y quedan sin estos archivos.",
    porOmision: true,
  },
  { clave: "incluirPdfComplemento", etiqueta: "PDF de los complementos", porOmision: true },
  {
    clave: "incluirExcel",
    etiqueta: "Excel de la seleccion",
    descripcion: "Las hojas Facturas y Complementos de lo seleccionado, en la raiz del ZIP.",
    porOmision: false,
  },
];

const LEYENDA = [
  ["No aplica", "Factura PUE: se cobra en una sola exhibicion y no lleva complemento."],
  ["Sin cobros", "Factura PPD que todavia no recibe ningun pago."],
  ["Falta complemento", "Factura PPD con cobros registrados y sin complemento vigente. Hallazgo."],
  ["Complemento sin timbrar", "El complemento existe en el sistema pero no tiene UUID: nunca se timbro, asi que para el SAT no ampara nada. Hallazgo."],
  ["Conciliada", "Lo amparado por los complementos timbrados coincide con lo cobrado."],
  ["Diferencia", "Los complementos amparan un importe distinto al cobrado."],
  ["PUE con complemento", "Una PUE no deberia tener complemento: revisar el metodo de pago."],
  ["Complemento sobre cancelada", "La factura esta cancelada pero su complemento sigue vigente."],
];

const dinero = (valor) => dineroDisabledMask(valor || 0);

const fmtFecha = (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : "");

class ReporteConciliacionFacturasPagos extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    exportando: false,
    data: [],
    totalRecords: 0,
    page: 1,
    pageSize: 50,
    sortField: null,
    sortOrder: null,
    totales: {},
    modoCompleto: false,
    truncado: false,
    clientes: [],
    formasDePago: new Map(),
    seleccion: [],
    // Los renglones seleccionados, no sólo sus llaves: la paginacion es del
    // servidor, asi que `data` sólo tiene la pagina actual y lo elegido en otras
    // paginas se perderia al armar la descarga.
    seleccionRegistros: new Map(),
    modalDescarga: false,
    descargando: false,
    progreso: null,
    // Los filtros con los que se trajo lo que se ve; paginar no debe tomar
    // ediciones del formulario que aun no se consultaron.
    filtros: {
      periodo: [moment().startOf("month"), moment().endOf("month")],
      ejeFecha: "factura",
      clientes: [],
      metodoPago: "todos",
      estatusConciliacion: "todos",
      numeroComplementos: "todos",
      incluirCanceladas: false,
      analizarTodo: false,
    },
  };

  async componentDidMount() {
    this.setState({ loading: true });
    await axios
      .post(
        "reportesFiscales/catalogosReportesFiscales",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            clientes: res.data.clientes || [],
            formasDePago: new Map(
              (res.data.formasDePago || []).map((forma) => [forma._id.toString(), forma])
            ),
          });
        }
      })
      .catch(() => message.error("No se pudieron cargar los catalogos de filtros"));

    this.consultar({ page: 1 });
  }

  atras() {
    this.props.history.goBack();
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  /** Submit del formulario: fija los filtros y regresa a la primera pagina. */
  aplicarFiltros(values) {
    this.setState(
      {
        filtros: { ...this.state.filtros, ...values },
        seleccion: [],
        seleccionRegistros: new Map(),
      },
      () => this.consultar({ page: 1 })
    );
  }

  /** Guarda el renglón completo, no sólo la llave, para poder descargar entre páginas. */
  cambiarSeleccion(seleccion, filas) {
    const registros = new Map(this.state.seleccionRegistros);
    for (const fila of filas || []) {
      if (fila && fila.ventaId) registros.set(fila.ventaId.toString(), fila);
    }
    const vigentes = new Set(seleccion.map((llave) => llave.toString()));
    for (const llave of [...registros.keys()]) {
      if (!vigentes.has(llave)) registros.delete(llave);
    }
    this.setState({ seleccion, seleccionRegistros: registros });
  }

  cuerpoConsulta(extra = {}) {
    const { filtros, page, pageSize, sortField, sortOrder } = this.state;
    return {
      empresaId: this.props.empresaId,
      periodo: filtros.periodo,
      ejeFecha: filtros.ejeFecha,
      clientes: filtros.clientes || [],
      metodoPago: filtros.metodoPago,
      estatusConciliacion: filtros.estatusConciliacion,
      numeroComplementos: filtros.numeroComplementos,
      incluirCanceladas: !!filtros.incluirCanceladas,
      analizarTodo: !!filtros.analizarTodo,
      page,
      pageSize,
      sortField,
      sortOrder,
      ...extra,
    };
  }

  consultar({ page, pageSize, sortField, sortOrder } = {}) {
    const nuevaPagina = page || this.state.page;
    const nuevoTamano = pageSize || this.state.pageSize;

    this.setState({
      loading: true,
      page: nuevaPagina,
      pageSize: nuevoTamano,
      sortField: sortField !== undefined ? sortField : this.state.sortField,
      sortOrder: sortOrder !== undefined ? sortOrder : this.state.sortOrder,
    }, () => {
      axios
        .post(
          "reportesFiscales/reporteConciliacionFacturasPagos",
          this.cuerpoConsulta(),
          { headers: this.props.headersToken }
        )
        .then((res) => {
          if (res.data.success === 2) {
            this.setState({
              data: res.data.data || [],
              totalRecords: res.data.totalRecords || 0,
              totales: res.data.totales || {},
              modoCompleto: !!res.data.modoCompleto,
              truncado: !!res.data.truncado,
              loading: false,
            });
          } else {
            message.error(res.data.message || "No se pudo cargar el reporte");
            this.setState({ loading: false });
          }
        })
        .catch(() => {
          message.error("Error de conexion");
          this.setState({ loading: false });
        });
    });
  }

  descripcionFiltros() {
    const { filtros, clientes } = this.state;
    const partes = [];
    if (filtros.periodo && filtros.periodo[0]) {
      partes.push(
        `${filtros.ejeFecha === "complemento" ? "Complementos" : "Facturas"} del ` +
        `${moment(filtros.periodo[0]).format("DD/MM/YYYY")} al ${moment(filtros.periodo[1]).format("DD/MM/YYYY")}`
      );
    }
    if (filtros.metodoPago && filtros.metodoPago !== "todos") partes.push(`Metodo ${filtros.metodoPago}`);
    if (filtros.estatusConciliacion && filtros.estatusConciliacion !== "todos") {
      partes.push(`Estatus: ${ETIQUETAS_ESTATUS[filtros.estatusConciliacion]}`);
    }
    const ETIQUETA_NUMERO = {
      sin: "Sin complemento timbrado",
      uno: "Con un solo complemento",
      varios: "Con varios complementos",
    };
    if (ETIQUETA_NUMERO[filtros.numeroComplementos]) {
      partes.push(ETIQUETA_NUMERO[filtros.numeroComplementos]);
    }
    if (filtros.clientes && filtros.clientes.length > 0) {
      const nombres = filtros.clientes
        .map((id) => (clientes.find((cliente) => cliente._id === id) || {}).nombre)
        .filter(Boolean);
      partes.push(`Clientes: ${nombres.join(", ")}`);
    }
    if (filtros.incluirCanceladas) partes.push("Incluye canceladas");
    return partes.join(" | ");
  }

  /**
   * Excel y PDF traen el periodo completo, no la pagina: se pide aparte con
   * `exportar` y el backend responde con el tope de seguridad aplicado.
   */
  exportar(formato) {
    this.setState({ exportando: true });
    axios
      .post(
        "reportesFiscales/reporteConciliacionFacturasPagos",
        this.cuerpoConsulta({ exportar: true }),
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ exportando: false });
        if (res.data.success !== 2) {
          message.error(res.data.message || "No se pudo generar el archivo");
          return;
        }
        const registros = res.data.data || [];
        if (registros.length === 0) {
          message.info("No hay facturas en el periodo con los filtros aplicados");
          return;
        }
        if (res.data.truncado) {
          message.warning(
            "El periodo excede el maximo del reporte: el archivo sale truncado. Acorta el rango de fechas."
          );
        }
        const params = {
          registros,
          empresaNombre: this.props.empresa,
          descripcionFiltros: this.descripcionFiltros(),
          totales: res.data.totales || {},
          formasDePago: this.state.formasDePago,
        };
        if (formato === "excel") descargarExcel(params);
        else descargarPdf(params);
      })
      .catch(() => {
        this.setState({ exportando: false });
        message.error("Error de conexion");
      });
  }

  registrosSeleccionados() {
    return this.state.seleccion
      .map((llave) => this.state.seleccionRegistros.get(llave.toString()))
      .filter(Boolean);
  }

  descargar(formatos) {
    const registros = this.registrosSeleccionados();
    this.setState({ descargando: true, progreso: null });

    descargarConciliacionMasivo({
      registros,
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      ...formatos,
      construirExcel: (seleccionados) =>
        excelConciliacionBlob({
          registros: seleccionados,
          empresaNombre: this.props.empresa,
          descripcionFiltros: this.descripcionFiltros(),
          formasDePago: this.state.formasDePago,
        }),
      onProgreso: (hechos, total) => this.setState({ progreso: { hechos, total } }),
    }).then((ok) => {
      this.setState({ descargando: false, progreso: null, modalDescarga: !ok });
    });
  }

  columnasComplementos() {
    return [
      { title: "Complemento", dataIndex: "folio", key: "folio", width: 130 },
      {
        title: "UUID timbrado",
        dataIndex: "uuid",
        key: "uuid",
        width: 300,
        render: (valor) =>
          valor ? (
            <span style={{ fontFamily: "monospace", fontSize: 11, userSelect: "all" }}>{valor}</span>
          ) : (
            <Tag color="red">Sin timbrar (no tiene UUID)</Tag>
          ),
      },
      {
        title: "Fecha pago",
        dataIndex: "fechaPago",
        key: "fechaPago",
        width: 110,
        align: "center",
        render: fmtFecha,
      },
      {
        title: "Forma de pago",
        dataIndex: "formaDePagoId",
        key: "formaDePagoId",
        width: 220,
        ellipsis: true,
        render: (valor) => etiquetaFormaDePago(this.state.formasDePago, valor),
      },
      {
        title: "Parc.",
        dataIndex: "parcialidad",
        key: "parcialidad",
        width: 70,
        align: "center",
        render: (valor) => (valor || ""),
      },
      {
        title: "Saldo anterior",
        dataIndex: "saldoAnterior",
        key: "saldoAnterior",
        width: 140,
        align: "right",
        render: dinero,
      },
      {
        title: "Importe pagado",
        dataIndex: "impPagado",
        key: "impPagado",
        width: 140,
        align: "right",
        render: (valor) => <b>{dinero(valor)}</b>,
      },
      {
        title: "Saldo insoluto",
        dataIndex: "saldoInsoluto",
        key: "saldoInsoluto",
        width: 140,
        align: "right",
        render: dinero,
      },
      {
        title: "Estado",
        dataIndex: "estado",
        key: "estado",
        width: 100,
        render: (valor, registro) => (
          <Space size={4}>
            <Tag color={valor === 0 ? "red" : "green"}>{valor === 0 ? "Cancelado" : "Vigente"}</Tag>
            {registro.desdeCfdi === false && (
              <Tooltip title="Los importes salen del pago, no del CFDI: el complemento no trae un CFDI legible.">
                <QuestionCircleOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ];
  }

  columnas() {
    return [
      {
        title: "Folio",
        dataIndex: "folio",
        key: "folio",
        width: 150,
        fixed: window.innerWidth > 1200 ? "left" : null,
        render: (valor, registro) => (
          <div>
            <div style={{ fontWeight: 600 }}>{valor}</div>
            <Tooltip title={registro.uuid}>
              <div style={{ fontSize: 10, color: "#8c8c8c" }}>
                {(registro.uuid || "").slice(0, 8)}...
              </div>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Fecha",
        dataIndex: "fechaFactura",
        key: "fechaFactura",
        width: 105,
        align: "center",
        sorter: true,
        render: fmtFecha,
      },
      {
        title: "Cliente",
        dataIndex: "cliente",
        key: "cliente",
        width: 250,
        ellipsis: true,
        sorter: true,
        render: (valor, registro) => (
          <div>
            <div>{valor}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{registro.rfc}</div>
          </div>
        ),
      },
      {
        title: "Metodo",
        dataIndex: "metodoDePago",
        key: "metodoDePago",
        width: 85,
        align: "center",
        render: (valor) => <Tag color={valor === "PPD" ? "blue" : "default"}>{valor}</Tag>,
      },
      {
        title: "Total",
        dataIndex: "total",
        key: "total",
        width: 140,
        align: "right",
        sorter: true,
        render: dinero,
      },
      {
        title: "Cobrado",
        dataIndex: "totalPagado",
        key: "totalPagado",
        width: 140,
        align: "right",
        sorter: true,
        render: dinero,
      },
      {
        title: "Pendiente",
        dataIndex: "pendiente",
        key: "pendiente",
        width: 140,
        align: "right",
        render: dinero,
      },
      {
        title: "Amparado",
        dataIndex: "amparado",
        key: "amparado",
        width: 140,
        align: "right",
        render: (valor, registro) =>
          registro.requiereComplemento ? <b>{dinero(valor)}</b> : "-",
      },
      {
        title: "Diferencia",
        dataIndex: "diferencia",
        key: "diferencia",
        width: 140,
        align: "right",
        render: (valor, registro) => {
          if (!registro.requiereComplemento) return "-";
          const hayDiferencia = Math.abs(valor || 0) > 0.01;
          return (
            <span style={{ color: hayDiferencia ? "#a8071a" : "#8c8c8c", fontWeight: hayDiferencia ? 600 : 400 }}>
              {dinero(valor)}
            </span>
          );
        },
      },
      {
        title: "Timbrados",
        dataIndex: "complementosTimbrados",
        key: "complementosTimbrados",
        width: 110,
        align: "center",
        render: (valor, registro) => (
          <Space size={4}>
            <span style={{ fontWeight: 600 }}>{valor || 0}</span>
            {registro.complementosSinTimbrar > 0 && (
              <Tooltip title={`${registro.complementosSinTimbrar} complemento(s) sin UUID: nunca se timbraron`}>
                <Tag color="red" style={{ marginRight: 0 }}>
                  {`${registro.complementosSinTimbrar} s/t`}
                </Tag>
              </Tooltip>
            )}
            {registro.complementosCancelados > 0 && (
              <Tooltip title={`${registro.complementosCancelados} complemento(s) cancelado(s)`}>
                <Tag color="volcano" style={{ marginRight: 0 }}>
                  {`${registro.complementosCancelados} canc`}
                </Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        // La columna del reporte: los UUID que amparan la factura, para cotejarlos
        // contra el SAT. `userSelect: all` deja copiar uno con un clic.
        title: "UUID complemento",
        dataIndex: "uuidsComplementos",
        key: "uuidsComplementos",
        width: 265,
        render: (uuids, registro) => {
          const lista = uuids || [];
          if (lista.length === 0) {
            if (!registro.requiereComplemento) {
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            }
            return (
              <span style={{ color: "#a8071a", fontSize: 12 }}>
                {registro.complementosSinTimbrar > 0 ? "Complemento sin UUID" : "Sin complemento"}
              </span>
            );
          }
          return (
            <div>
              {lista.length > 1 && (
                <Tag color="blue" style={{ marginBottom: 2 }}>
                  {`${lista.length} complementos`}
                </Tag>
              )}
              {lista.slice(0, 3).map((uuid) => (
                <Tooltip key={uuid} title={uuid}>
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: 11,
                      userSelect: "all",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {uuid}
                  </div>
                </Tooltip>
              ))}
              {lista.length > 3 && (
                <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                  {`+${lista.length - 3} mas (expande el renglon)`}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Estatus",
        dataIndex: "estatus",
        key: "estatus",
        width: 200,
        render: (valor, registro) => (
          <Tag color={COLOR_ESTATUS[valor] || "default"}>
            {registro.estatusEtiqueta || ETIQUETAS_ESTATUS[valor] || valor}
          </Tag>
        ),
      },
      {
        title: "Factura",
        dataIndex: "estadoFactura",
        key: "estadoFactura",
        width: 130,
        render: (valor) =>
          valor === 0
            ? <Tag color="red">Cancelada</Tag>
            : <span style={{ fontSize: 12 }}>{ESTADO_FACTURA[valor] || ""}</span>,
      },
    ];
  }

  tarjetasResumen() {
    const { totales, modoCompleto } = this.state;
    const auditoria = (valor) => (modoCompleto ? String(valor || 0) : "—");

    const tarjetas = [
      { etiqueta: "Facturas", valor: String(totales.facturas || 0) },
      // Las PPD son las unicas que llevan complemento; las PUE salen por resta y no
      // gastan una tarjeta.
      { etiqueta: "PPD", valor: String(totales.facturasPpd || 0) },
      { etiqueta: "Cobrado", valor: dinero(totales.totalPagado) },
      {
        etiqueta: "Amparado",
        valor: modoCompleto ? dinero(totales.amparado) : "—",
        color: "#005F6B",
      },
      { etiqueta: "Complementos timbrados", valor: auditoria(totales.complementos) },
      { etiqueta: "Falta complemento", valor: auditoria(totales.sinComplemento), color: "#a8071a" },
      { etiqueta: "Sin timbrar", valor: auditoria(totales.sinTimbrar), color: "#a8071a" },
      { etiqueta: "Con diferencia", valor: auditoria(totales.conDiferencia), color: "#d46b08" },
    ];

    return (
      <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
        {tarjetas.map((tarjeta) => (
          <Col key={tarjeta.etiqueta} xs={12} sm={8} md={6} lg={3}>
            <div
              style={{
                backgroundColor: "white",
                borderRadius: 5,
                padding: "10px 12px",
                borderTop: `3px solid ${tarjeta.color || "#00BFD6"}`,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                height: "100%",
              }}
            >
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>{tarjeta.etiqueta}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: tarjeta.color || "#005F6B" }}>
                {tarjeta.valor}
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  }

  render() {
    const { loading, exportando, data, totalRecords, page, pageSize, modoCompleto, truncado } = this.state;
    const seleccionados = this.state.seleccion.length;

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteConciliacionFacturasPagos"
          onFinish={this.aplicarFiltros.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={this.state.filtros}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Conciliacion Facturas - Pagos"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <Space>
                {seleccionados > 0 && (
                  <ButtonItem
                    blanco
                    icon={<DownloadOutlined />}
                    onClick={() => this.setState({ modalDescarga: true })}
                  >
                    {`Descargar (${seleccionados})`}
                  </ButtonItem>
                )}
                {data.length > 0 && (
                  <ButtonItem
                    blanco
                    icon={<FileExcelOutlined />}
                    loading={exportando}
                    onClick={() => this.exportar("excel")}
                  >
                    Excel
                  </ButtonItem>
                )}
                {data.length > 0 && (
                  <ButtonItem
                    blanco
                    icon={<FilePdfOutlined />}
                    loading={exportando}
                    onClick={() => this.exportar("pdf")}
                  >
                    PDF
                  </ButtonItem>
                )}
                <ButtonItem type="primary" htmlType="submit">
                  Consultar
                </ButtonItem>
              </Space>
            }
          />

          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                marginBottom: 10,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <Row gutter={16}>
                    <Col span={10}>
                      <FormItem name="periodo" required noStyle margin>
                        <RangePickerItem />
                      </FormItem>
                    </Col>
                    <Col span={8}>
                      <FormItem name="ejeFecha" noStyle margin>
                        <SelectItem width="95%" placeholder="El periodo aplica a">
                          <Option value="factura">Fecha de factura</Option>
                          <Option value="complemento">Fecha de complemento</Option>
                        </SelectItem>
                      </FormItem>
                      <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                        Por factura: que facture en el periodo y que tan cubierto esta.
                        Por complemento: que complementos timbre y contra que facturas.
                      </div>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabKey="2" key="2" forceRender tab="Clientes">
                  <Row>
                    <Col span={16}>
                      <FormItem name="clientes" noStyle margin>
                        <SelectItem width="90%" mode="multiple" placeholder="Todos los clientes">
                          {/* El children tiene que ser UNA cadena: el filterOption de
                              SelectItem hace children.toLowerCase() y con un arreglo
                              de nodos la busqueda deja de encontrar nada. */}
                          {this.state.clientes.map((cliente) => (
                            <Option value={cliente._id} key={cliente._id}>
                              {`${cliente.nombre}${cliente.rfc ? ` - ${cliente.rfc}` : ""}`}
                            </Option>
                          ))}
                        </SelectItem>
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabKey="3" key="3" forceRender tab="Conciliacion">
                  <Row gutter={16}>
                    <Col span={5}>
                      <FormItem name="metodoPago" noStyle margin>
                        <SelectItem width="95%" placeholder="Metodo de pago">
                          <Option value="todos">Todos los metodos</Option>
                          <Option value="PPD">Solo PPD</Option>
                          <Option value="PUE">Solo PUE</Option>
                        </SelectItem>
                      </FormItem>
                    </Col>
                    <Col span={7}>
                      <FormItem name="estatusConciliacion" noStyle margin>
                        <SelectItem width="95%" placeholder="Estatus de conciliacion">
                          <Option value="todos">Todos los estatus</Option>
                          {Object.keys(ETIQUETAS_ESTATUS).map((clave) => (
                            <Option value={clave} key={clave}>{ETIQUETAS_ESTATUS[clave]}</Option>
                          ))}
                        </SelectItem>
                      </FormItem>
                      <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                        Filtrar por estatus analiza el periodo completo: tarda mas.
                      </div>
                    </Col>
                    <Col span={6}>
                      <FormItem name="numeroComplementos" noStyle margin>
                        <SelectItem width="95%" placeholder="Complementos timbrados">
                          <Option value="todos">Con y sin complemento</Option>
                          <Option value="sin">Sin complemento timbrado</Option>
                          <Option value="uno">Con un solo complemento</Option>
                          <Option value="varios">Con varios complementos</Option>
                        </SelectItem>
                      </FormItem>
                      <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                        Cuenta solo los que tienen UUID. Tambien analiza el periodo completo.
                      </div>
                    </Col>
                    <Col span={3}>
                      <FormItem name="incluirCanceladas" noStyle margin valuePropName="checked">
                        <Checkbox>Incluir canceladas</Checkbox>
                      </FormItem>
                    </Col>
                    <Col span={3}>
                      <FormItem name="analizarTodo" noStyle margin valuePropName="checked">
                        <Checkbox>Analizar todo</Checkbox>
                      </FormItem>
                      <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                        Llena las cifras de auditoria. Cuesta mas.
                      </div>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabKey="4" key="4" tab="Leyenda">
                  <Table
                    size="small"
                    rowKey={(fila) => fila[0]}
                    showHeader={false}
                    pagination={false}
                    dataSource={LEYENDA}
                    columns={[
                      {
                        key: "estatus",
                        width: 220,
                        render: (fila) => {
                          const clave = Object.keys(ETIQUETAS_ESTATUS)
                            .find((llave) => ETIQUETAS_ESTATUS[llave] === fila[0]);
                          return <Tag color={COLOR_ESTATUS[clave] || "default"}>{fila[0]}</Tag>;
                        },
                      },
                      { key: "descripcion", render: (fila) => fila[1] },
                    ]}
                  />
                </TabPane>
              </Tabs>
            </div>

            {this.tarjetasResumen()}

            {!modoCompleto && (
              <Alert
                style={{ marginBottom: 10 }}
                type="info"
                showIcon
                message="Amparado, Falta complemento y Con diferencia se calculan sobre el periodo completo"
                description="La consulta paginada solo revisa los complementos de la pagina que ves. Marca 'Analizar periodo completo' en la pestana Conciliacion, o filtra por un estatus, para llenar esas cifras. El Excel y el PDF siempre traen el periodo completo."
              />
            )}

            {truncado && (
              <Alert
                style={{ marginBottom: 10 }}
                type="warning"
                showIcon
                message="El periodo excede el maximo del reporte y se trunco"
                description="Acorta el rango de fechas o filtra por cliente para ver todo."
              />
            )}

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Table
                size="small"
                rowKey={(registro) => registro.ventaId.toString()}
                columns={this.columnas()}
                dataSource={data}
                loading={loading}
                scroll={{ x: "max-content" }}
                rowSelection={{
                  selectedRowKeys: this.state.seleccion,
                  onChange: this.cambiarSeleccion.bind(this),
                  // La paginacion es del servidor: sin esto antd tira las llaves
                  // de las paginas que ya no estan en pantalla.
                  preserveSelectedRowKeys: true,
                }}
                expandable={{
                  expandedRowRender: (registro) => (
                    <Table
                      size="small"
                      rowKey={(complemento, indice) => `${complemento.pagoId}-${indice}`}
                      columns={this.columnasComplementos()}
                      dataSource={registro.complementos}
                      pagination={false}
                      scroll={{ x: "max-content" }}
                    />
                  ),
                  rowExpandable: (registro) =>
                    registro.complementos && registro.complementos.length > 0,
                }}
                pagination={{
                  current: page,
                  pageSize,
                  total: totalRecords,
                  showSizeChanger: true,
                  pageSizeOptions: PAGINACION.map(String),
                  showTotal: (total) => `${total} factura(s)`,
                }}
                // Paginacion y orden son del servidor: los dos entran por aqui.
                onChange={(paginacion, filtrosTabla, orden) =>
                  this.consultar({
                    page: paginacion.current,
                    pageSize: paginacion.pageSize,
                    sortField: orden && orden.order ? orden.columnKey : null,
                    sortOrder: (orden && orden.order) || null,
                  })
                }
              />
            </div>
          </Spin>
        </Form>

        <ModalDescargarDocumentos
          visible={this.state.modalDescarga}
          total={seleccionados}
          opciones={OPCIONES_DESCARGA}
          descripcion={
            seleccionados === 1
              ? "Se descargara un ZIP con una carpeta para la factura seleccionada y sus complementos."
              : `Se descargara un ZIP con una carpeta por cada una de las ${seleccionados} facturas seleccionadas y sus complementos.`
          }
          loading={this.state.descargando}
          progreso={this.state.progreso}
          onClose={() => this.setState({ modalDescarga: false })}
          onConfirm={this.descargar.bind(this)}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(ReporteConciliacionFacturasPagos);
